import type { ProgressEvent } from 'superagent';

import { getSignedUrl, triggerBuilds, uploadFile } from '../../../api/componentUploader';
import { useMutation } from '../../../ReactQuery';
import type { FileUploadRequest } from '../../../../typings/api';
import type { Team } from '../../../types';

/**
 * Parameters for uploading and building an archive.
 */
export type UploadAndBuildArchiveParams = {
  /**
   * The archive file to be uploaded and built.
   */
  file: File;

  /**
   * The name (project) of the archive being uploaded.
   */
  name: string;
  /**
   * The version (revision) of the archive being uploaded.
   */
  version: string;

  /**
   * Optional array of teams to associate with the uploaded component.
   * NOTE: This is only relevant for top-level project builds, not dependency builds.
   */
  teams?: Array<Pick<Team, 'id' | 'name' | 'organizationId'>>;
  /**
   * Optional boolean to force rebuild of the component.
   */
  shouldForceRebuild?: boolean;

  /**
   * Optional additional options to be passed to the build.
   */
  options?: FileUploadRequest;

  /**
   * Optional callback function that is called to update progress during an archive upload.
   */
  onArchiveUploadProgress?: (progressEvent: ProgressEvent) => void;
};

const uploadAndBuildArchive = async (params: UploadAndBuildArchiveParams) => {
  const { file, name, version, teams, shouldForceRebuild, options, onArchiveUploadProgress } = params;

  // Step 1: Get signed URL and upload the archive to cloud storage.
  const uploadUrl = await getSignedUrl(name, version);
  await uploadFile(file, uploadUrl, onArchiveUploadProgress);

  // Step 2: Trigger a build for the uploaded archive.
  await triggerBuilds(
    {
      archives: [{ packageSpec: name, revision: version }],
      selectedTeams: teams ?? [],
      forceRebuild: shouldForceRebuild,
    },
    options,
  );
};

/**
 * Generic, reusable hook for uploading and building an archive component.
 */
export default function useUploadAndBuildArchive() {
  return useMutation({
    mutationFn: async (params: UploadAndBuildArchiveParams) => uploadAndBuildArchive(params),
  });
}
